/**
 * MiniTimelineWidget — strip horizontale de la journée
 */
import React, { memo, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { haptic } from '../../shared/utils/haptics';

function timeToMin(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

const COLORS = {
  vital:    '#ef4444',
  chore:    '#f59e0b',
  pleasure: '#10b981',
  reward:   '#8b5cf6',
  work:     '#3b82f6',
  health:   '#14b8a6',
};

const Dot = memo(function Dot({ task, isCurrent, isCompleted, onTap }) {
  const color = COLORS[task.category] || '#71717a';

  return (
    <motion.button
      onClick={() => { haptic.tap(); onTap?.(task); }}
      className="flex-shrink-0 flex flex-col items-center gap-1.5 snap-center"
      style={{ width: '60px' }}
      whileTap={{ scale: 0.88 }}
      aria-label={task.task}
    >
      {/* Pastille */}
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center relative"
        style={{
          background:   isCompleted ? 'rgba(255,255,255,0.04)' : `${color}22`,
          border:       `2px solid ${isCompleted ? 'rgba(255,255,255,0.08)' : isCurrent ? color : `${color}55`}`,
          boxShadow:    isCurrent ? `0 0 12px ${color}66` : 'none',
        }}
      >
        {isCompleted && <Check className="w-3.5 h-3.5" strokeWidth={2.5} style={{ color: 'rgba(255,255,255,0.25)' }} />}
        {!isCompleted && !isCurrent && <div className="w-2 h-2 rounded-full" style={{ background: color, opacity: 0.55 }} />}
        {isCurrent && (
          <>
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{ background: color, opacity: 0.12 }}
              animate={{ scale: [1, 1.8, 1] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
            />
          </>
        )}
      </div>

      {/* Heure */}
      <span className="text-[10px] font-mono leading-none tabular-nums"
        style={{
          color: isCurrent ? 'rgba(255,255,255,0.72)' : isCompleted ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.35)',
          fontWeight: isCurrent ? 700 : 400,
        }}>
        {task.startTime || task.time || '—'}
      </span>

      {/* Titre tronqué */}
      <span className="text-[9px] text-center leading-tight w-full px-0.5 line-clamp-2"
        style={{
          color: isCurrent ? 'rgba(255,255,255,0.60)' : isCompleted ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.30)',
          fontWeight: isCurrent ? 600 : 400,
        }}>
        {task.task}
      </span>
    </motion.button>
  );
});

const NowMarker = memo(function NowMarker() {
  return (
    <div className="flex-shrink-0 flex flex-col items-center gap-1.5 snap-center" style={{ width: '32px' }}>
      <div className="relative flex items-center justify-center w-8 h-8">
        <div className="w-0.5 h-5 rounded-full" style={{ background: 'rgba(255,255,255,0.20)' }} />
        <motion.div
          className="absolute top-1 w-2 h-2 rounded-full bg-white/60"
          animate={{ opacity: [1, 0.2, 1] }}
          transition={{ duration: 1.6, repeat: Infinity }}
        />
      </div>
      <span className="text-[8px] font-bold uppercase tracking-widest"
        style={{ color: 'rgba(255,255,255,0.28)' }}>
        now
      </span>
    </div>
  );
});

export const MiniTimelineWidget = memo(function MiniTimelineWidget({
  todayTasks, isTaskCompleted, currentDayIndex, currentTask, onOpenDetail,
}) {
  const scrollRef    = useRef(null);
  const nowMarkerRef = useRef(null);

  useEffect(() => {
    const c = scrollRef.current;
    const m = nowMarkerRef.current;
    if (c && m) {
      c.scrollTo({ left: Math.max(0, m.offsetLeft - c.offsetWidth / 2 + m.offsetWidth / 2), behavior: 'smooth' });
    }
  }, [todayTasks]);

  if (!todayTasks?.length) {
    return (
      <div className="flex items-center justify-center h-14 rounded-xl"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.18)' }}>Aucune tâche planifiée</span>
      </div>
    );
  }

  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const pastTasks    = todayTasks.filter(t => (timeToMin(t.startTime || t.time) + (t.duration || 30)) <= nowMin);
  const currentList  = todayTasks.filter(t => { const s = timeToMin(t.startTime || t.time); return nowMin >= s && nowMin < s + (t.duration || 30); });
  const futureTasks  = todayTasks.filter(t => timeToMin(t.startTime || t.time) > nowMin);
  const doneCount    = pastTasks.filter(t => isTaskCompleted?.(currentDayIndex, t)).length + currentList.filter(t => isTaskCompleted?.(currentDayIndex, t)).length;

  return (
    <div role="region" aria-label="Planning du jour">
      {/* Compteur */}
      <div className="flex items-center justify-between mb-2 px-0.5">
        <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.22)' }}>
          Aujourd'hui
        </span>
        <span className="text-[10px] font-mono" style={{ color: 'rgba(255,255,255,0.20)' }}>
          {doneCount}/{todayTasks.length}
        </span>
      </div>

      {/* Strip */}
      <div
        ref={scrollRef}
        className="flex items-start gap-1 overflow-x-auto pb-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollSnapType: 'x proximity' }}
      >
        {pastTasks.map(t => (
          <Dot key={t.id} task={t} isCurrent={false}
            isCompleted={isTaskCompleted?.(currentDayIndex, t)} onTap={onOpenDetail} />
        ))}
        {currentList.map(t => (
          <Dot key={t.id} task={t} isCurrent={true} isCompleted={false} onTap={onOpenDetail} />
        ))}
        {currentList.length === 0
          ? <div ref={nowMarkerRef}><NowMarker /></div>
          : <div ref={nowMarkerRef} className="w-0 flex-shrink-0" />
        }
        {futureTasks.map(t => (
          <Dot key={t.id} task={t} isCurrent={false}
            isCompleted={isTaskCompleted?.(currentDayIndex, t)} onTap={onOpenDetail} />
        ))}
      </div>
    </div>
  );
});

export default MiniTimelineWidget;
